import React, { useEffect, useState } from "react";
import { ExternalCar } from "../../types";
import { useTheme } from "../../contexts/ThemeContext";
import { useWorkshop } from "../../contexts/WorkshopContext";
import ExternalCarService from "../../services/externalCarService";

const VehicleList: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { selectedWorkshop } = useWorkshop();
  const [vehicles, setVehicles] = useState<ExternalCar[]>([]);
  const [filteredVehicles, setFilteredVehicles] = useState<ExternalCar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (selectedWorkshop) {
      loadVehicles();
    }
  }, [selectedWorkshop]);

  useEffect(() => {
    filterVehicles();
  }, [vehicles, searchTerm, statusFilter]);

  const loadVehicles = async () => {
    if (!selectedWorkshop) {
      setError("Please select a workshop first");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await ExternalCarService.getCars({
        workshop_id: selectedWorkshop.id,
        limit: 1000,
      });
      setVehicles(response.data);
      setTotalCount(response.pagination.total);
    } catch (error) {
      console.error("Error loading vehicles:", error);
      setError("Error loading vehicles from external database");
    } finally {
      setLoading(false);
    }
  };

  const filterVehicles = () => {
    let filtered = vehicles;

    if (searchTerm) {
      filtered = filtered.filter(
        (vehicle) =>
          vehicle.number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vehicle.tank?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vehicle.plate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vehicle.name_driver?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter) {
      filtered = filtered.filter((vehicle) => vehicle.status === statusFilter);
    }

    setFilteredVehicles(filtered);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "available":
        return "bg-green-100 text-green-800";
      case "working":
        return "bg-blue-100 text-blue-800";
      case "maintenance":
        return "bg-yellow-100 text-yellow-800";
      case "no_drive":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "available":
        return "Available";
      case "working":
        return "Working";
      case "maintenance":
        return "Maintenance";
      case "no_drive":
        return "No Drive";
      default:
        return status;
    }
  };

  if (!selectedWorkshop) {
    return (
      <div className="mt-3">
        <div
          className={`rounded-lg shadow p-8 text-center ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          <svg
            className={`mx-auto h-12 w-12 mb-4 ${
              isDarkMode ? "text-gray-600" : "text-gray-400"
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3
            className={`text-lg font-medium mb-2 ${
              isDarkMode ? "text-gray-300" : "text-gray-900"
            }`}
          >
            No Workshop Selected
          </h3>
          <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
            Please select a workshop from the dropdown to view vehicles.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <div
        className={`rounded-lg shadow p-4 mb-6 ${
          isDarkMode ? "bg-gray-800" : "bg-white"
        }`}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              className={`block text-sm font-medium mb-1 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Search
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Plate, brand, color, description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`block w-full pl-10 pr-3 py-2 border rounded-md leading-5 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 sm:text-sm ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                }`}
              />
            </div>
          </div>

          <div>
            <label
              className={`block text-sm font-medium mb-1 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`block w-full py-2 px-3 border rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              }`}
            >
              <option value="">All</option>
              <option value="available">Available</option>
              <option value="in_use">In Use</option>
              <option value="maintenance">Maintenance</option>
              <option value="out_of_service">Out of Service</option>
            </select>
          </div>
        </div>

        {(searchTerm || statusFilter) && (
          <div className="mt-4">
            <button
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("");
              }}
              className={`text-sm ${
                isDarkMode
                  ? "text-orange-400 hover:text-orange-300"
                  : "text-orange-600 hover:text-orange-800"
              }`}
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div className="text-center py-12">
          <div
            className={`inline-block animate-spin rounded-full h-8 w-8 border-b-2 ${
              isDarkMode ? "border-orange-400" : "border-orange-600"
            }`}
          ></div>
          <p
            className={`mt-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            Loading vehicles...
          </p>
        </div>
      )}

      {error && (
        <div
          className={`border px-4 py-3 rounded mb-4 ${
            isDarkMode
              ? "bg-red-900/20 border-red-500 text-red-300"
              : "bg-red-50 border-red-400 text-red-700"
          }`}
        >
          {error}
        </div>
      )}

      {!loading && !error && (
        <div
          className={`mb-4 text-sm ${
            isDarkMode ? "text-gray-400" : "text-gray-600"
          }`}
        >
          Showing {filteredVehicles.length} of {totalCount} vehicles
        </div>
      )}

      {!loading && !error && (
        <div
          className={`rounded-lg shadow overflow-hidden ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          <div className="overflow-x-auto">
            <table
              className={`min-w-full divide-y ${
                isDarkMode ? "divide-gray-700" : "divide-gray-200"
              }`}
            >
              <thead className={isDarkMode ? "bg-gray-900" : "bg-gray-50"}>
                <tr>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    ID
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Number
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Tank
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Plate
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Driver Name
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Phone
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Status
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Oil Chain
                  </th>
                </tr>
              </thead>
              <tbody
                className={`divide-y ${
                  isDarkMode
                    ? "bg-gray-800 divide-gray-700"
                    : "bg-white divide-gray-200"
                }`}
              >
                {filteredVehicles.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className={`px-6 py-12 text-center ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <p className="mt-2">No vehicles found</p>
                    </td>
                  </tr>
                ) : (
                  filteredVehicles.map((vehicle) => (
                    <tr
                      key={vehicle.id}
                      className={
                        isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"
                      }
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }`}
                        >
                          {vehicle.id}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div
                          className={`text-sm font-semibold ${
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }`}
                        >
                          {vehicle.number || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div
                          className={`text-sm ${
                            isDarkMode ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {vehicle.tank || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div
                          className={`text-sm font-semibold ${
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }`}
                        >
                          {vehicle.plate}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {vehicle.name_driver || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {vehicle.phone_driver || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                            vehicle.status
                          )}`}
                        >
                          {getStatusText(vehicle.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {vehicle.oil_chain || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleList;
